import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ManageInfoPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gb, setGb] = useState([]);
  const [gbPending, setGbPending] = useState(0);
  const loadGb = () => api.get("/admin/guestbook").then((r) => { setGb(r.data.items); setGbPending(r.data.pending); }).catch(() => {});

  useEffect(() => { api.get("/info-page").then((r) => setInfo(r.data)).catch(() => {}); loadGb(); }, []);
  if (!info) return null;

  const delGuest = async (id) => {
    if (!window.confirm("Hapus pesan buku tamu ini?")) return;
    try { await api.delete(`/guestbook/${id}`); await loadGb(); toast.success("Pesan dihapus"); }
    catch { toast.error("Gagal menghapus"); }
  };

  const approveGuest = async (id) => {
    try { await api.post(`/guestbook/${id}/approve`); await loadGb(); toast.success("Pesan disetujui & tampil publik"); }
    catch { toast.error("Gagal menyetujui"); }
  };

  const set = (k, v) => setInfo({ ...info, [k]: v });

  const addPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => set("photos", [...(info.photos || []), reader.result]);
    reader.readAsDataURL(f);
  };

  const anns = info.announcements || [];
  const setAnn = (i, k, v) => set("announcements", anns.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  const addAnn = () => set("announcements", [...anns, { title: "", text: "", poster: "" }]);
  const delAnn = (i) => set("announcements", anns.filter((_, j) => j !== i));
  const setAnnPoster = (i, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setAnn(i, "poster", reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/info-page", info);
      setInfo(data); toast.success("Halaman informasi disimpan");
    } catch (e) { toast.error("Gagal menyimpan"); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9FBE7] pb-28" data-testid="manage-info-screen">
      <div className="bg-primary text-white px-5 pt-6 pb-5 rounded-b-[2rem] flex items-center gap-3">
        <button data-testid="mi-back" onClick={() => navigate("/")} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><ArrowLeft size={20} weight="bold" /></button>
        <h1 className="font-heading font-bold text-lg">Kelola Halaman Informasi</h1>
      </div>

      <div className="px-5 mt-5 space-y-5">
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">Teks Informasi</label>
          <textarea data-testid="mi-text" value={info.text} onChange={(e) => set("text", e.target.value)} rows={5}
            className="w-full mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 outline-none focus:border-primary text-sm" />
        </div>

        {/* Pengumuman (poster + teks) */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">Pengumuman (poster + teks)</label>
          <div className="space-y-4 mt-2">
            {anns.map((a, i) => (
              <div key={i} data-testid={`mi-ann-${i}`} className="border border-stone-200 rounded-xl p-3 relative">
                <button data-testid={`mi-ann-del-${i}`} onClick={() => delAnn(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-100 text-red-600 flex items-center justify-center"><Trash size={14} weight="bold" /></button>
                <input data-testid={`mi-ann-title-${i}`} value={a.title || ""} onChange={(e) => setAnn(i, "title", e.target.value)} placeholder="Judul pengumuman"
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg p-2.5 outline-none focus:border-primary text-sm mb-2" />
                <textarea data-testid={`mi-ann-text-${i}`} value={a.text || ""} onChange={(e) => setAnn(i, "text", e.target.value)} rows={3} placeholder="Isi pengumuman"
                  className="w-full bg-stone-50 border border-stone-200 rounded-lg p-2.5 outline-none focus:border-primary text-sm mb-2" />
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <p className="text-[11px] text-stone-500 mb-0.5">Tampil dari (opsional)</p>
                    <input type="date" data-testid={`mi-ann-start-${i}`} value={a.start_date || ""} onChange={(e) => setAnn(i, "start_date", e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg p-2 outline-none focus:border-primary text-xs" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[11px] text-stone-500 mb-0.5">Sampai (opsional)</p>
                    <input type="date" data-testid={`mi-ann-end-${i}`} value={a.end_date || ""} onChange={(e) => setAnn(i, "end_date", e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg p-2 outline-none focus:border-primary text-xs" />
                  </div>
                </div>
                {a.poster ? (
                  <div className="relative">
                    <img src={a.poster} alt="" className="w-full rounded-lg object-cover" />
                    <button data-testid={`mi-ann-poster-del-${i}`} onClick={() => setAnn(i, "poster", "")} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center"><Trash size={13} weight="bold" /></button>
                  </div>
                ) : (
                  <label data-testid={`mi-ann-poster-${i}`} className="flex items-center gap-2 text-primary text-sm font-semibold cursor-pointer">
                    <Plus size={16} weight="bold" /> Tambah Poster<input type="file" accept="image/*" onChange={(e) => setAnnPoster(i, e)} className="hidden" />
                  </label>
                )}
              </div>
            ))}
            <button data-testid="mi-ann-add" onClick={addAnn} className="text-primary text-sm font-semibold flex items-center gap-1"><Plus size={15} weight="bold" /> Tambah Pengumuman</button>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">Dokumentasi Kegiatan Sekolah</label>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {(info.photos || []).map((p, i) => (
              <div key={i} className="relative">
                <img src={p} alt="" loading="lazy" className="w-full aspect-square object-cover rounded-xl" />
                <button data-testid={`mi-photo-del-${i}`} onClick={() => set("photos", info.photos.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center"><Trash size={13} weight="bold" /></button>
              </div>
            ))}
            <label data-testid="mi-photo-add" className="aspect-square rounded-xl border-2 border-dashed border-stone-300 flex items-center justify-center cursor-pointer text-stone-400">
              <Plus size={22} /><input type="file" accept="image/*" onChange={addPhoto} className="hidden" />
            </label>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">URL Pendaftaran</label>
          <input data-testid="mi-regurl" value={info.registration_url} onChange={(e) => set("registration_url", e.target.value)}
            placeholder="https://..." className="w-full mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 outline-none focus:border-primary text-sm" />
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">Video YouTube</label>
          {(info.videos || []).map((v, i) => (
            <div key={i} className="flex gap-2 mt-2">
              <input data-testid={`mi-video-${i}`} value={v} onChange={(e) => set("videos", info.videos.map((x, j) => j === i ? e.target.value : x))}
                placeholder="Link YouTube" className="flex-1 bg-stone-50 border border-stone-200 rounded-xl p-2.5 outline-none focus:border-primary text-sm" />
              <button data-testid={`mi-video-del-${i}`} onClick={() => set("videos", info.videos.filter((_, j) => j !== i))} className="w-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center"><Trash size={15} weight="bold" /></button>
            </div>
          ))}
          <button data-testid="mi-video-add" onClick={() => set("videos", [...(info.videos || []), ""])} className="mt-2 text-primary text-sm font-semibold flex items-center gap-1"><Plus size={15} weight="bold" /> Tambah Video</button>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">Nomor WhatsApp (mis. 6208126902233)</label>
          <input data-testid="mi-whatsapp" value={info.whatsapp} onChange={(e) => set("whatsapp", e.target.value)}
            className="w-full mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 outline-none focus:border-primary text-sm" />
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">Instagram</label>
          <input data-testid="mi-instagram-url" value={info.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)}
            placeholder="https://instagram.com/..." className="w-full mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 outline-none focus:border-primary text-sm" />
          <input data-testid="mi-instagram-text" value={info.instagram_text || ""} onChange={(e) => set("instagram_text", e.target.value)}
            placeholder="Keterangan (mis. @muslimkids.school)" className="w-full mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 outline-none focus:border-primary text-sm" />
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <label className="font-semibold text-stone-700 text-sm">YouTube (Channel)</label>
          <input data-testid="mi-youtube-url" value={info.youtube_url || ""} onChange={(e) => set("youtube_url", e.target.value)}
            placeholder="https://youtube.com/@..." className="w-full mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 outline-none focus:border-primary text-sm" />
          <input data-testid="mi-youtube-text" value={info.youtube_text || ""} onChange={(e) => set("youtube_text", e.target.value)}
            placeholder="Keterangan channel" className="w-full mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 outline-none focus:border-primary text-sm" />
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="mi-guestbook">
          <div className="flex items-center gap-2">
            <label className="font-semibold text-stone-700 text-sm">Buku Tamu ({gb.length})</label>
            {gbPending > 0 && <span data-testid="mi-gb-pending" className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600">{gbPending} menunggu persetujuan</span>}
          </div>
          <div className="space-y-2 mt-2">
            {gb.length === 0 && <p className="text-stone-400 text-sm">Belum ada pesan buku tamu.</p>}
            {gb.map((g) => (
              <div key={g.id} data-testid={`mi-gb-${g.id}`} className={`border rounded-xl p-3 flex items-start justify-between gap-2 ${g.approved ? "border-stone-200" : "border-amber-300 bg-amber-50"}`}>
                <div className="min-w-0">
                  {!g.approved && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 mb-1 inline-block">MENUNGGU PERSETUJUAN</span>}
                  <p className="font-semibold text-stone-800 text-sm truncate">{g.name}{g.address ? <span className="text-stone-400 font-normal"> · {g.address}</span> : null}</p>
                  <p className="text-stone-600 text-sm mt-0.5 whitespace-pre-line break-words">{g.message}</p>
                  <p className="text-stone-400 text-[11px] mt-1">{new Date(g.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</p>
                </div>
                <div className="shrink-0 flex flex-col gap-1.5">
                  {!g.approved && (
                    <button data-testid={`mi-gb-approve-${g.id}`} onClick={() => approveGuest(g.id)} className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center" title="Setujui"><CheckCircle size={16} weight="bold" /></button>
                  )}
                  <button data-testid={`mi-gb-del-${g.id}`} onClick={() => delGuest(g.id)} className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center" title="Hapus"><Trash size={14} weight="bold" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button data-testid="mi-save" onClick={save} disabled={saving}
          className="w-full bg-primary text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
          <FloppyDisk size={18} weight="bold" /> {saving ? "Menyimpan..." : "Simpan"}
        </button>
      </div>
    </div>
  );
}
